import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { baseUrl } from 'constants/baseUrl';
import storageService from 'services/storageService';
import { authHeader } from 'constants/authenticationHeader';
import { TextInput } from 'components/form/textInput';
import { Form } from '../form/form';
import { FormProvider, useForm } from 'react-hook-form';
import yupSchemaService from 'services/yupSchemaService';
import { yupResolver } from '@hookform/resolvers/yup';

const emptyCredentials = {
  ghost_api_url: '',
  ghost_admin_api_key: '',
  ghost_content_api_key: '',
};

// Ghost credentials belong to the company: every user of the company shares them.
const toCredentials = (company = {}) => ({
  ghost_api_url: (company.ghost_api_url || '').trim(),
  ghost_admin_api_key: (company.ghost_admin_api_key || '').trim(),
  ghost_content_api_key: (company.ghost_content_api_key || '').trim(),
});

const isConnected = (credentials) =>
  credentials.ghost_api_url !== '' && credentials.ghost_admin_api_key.includes(':');

const IntegrationPage = () => {
  // 'load' | 'save' | 'sync' | 'reset' | null
  const [busy, setBusy] = useState('load');
  const [companyName, setCompanyName] = useState('');
  const [saved, setSaved] = useState(emptyCredentials);

  const form = useForm({
    resolver: yupResolver(yupSchemaService.companyIntegrationSchema()),
    mode: 'all',
    defaultValues: emptyCredentials,
  });

  const connected = isConnected(saved);
  const hasUnsavedChanges = form.formState.isDirty;

  const fetchData = async () => {
    setBusy('load');
    try {
      const response = await axios.get(`${baseUrl}/rest/organization/info`, {
        headers: {
          common: {
            AuthAPI: `Bearer ${storageService.getToken()}`,
          },
        },
      });
      const company = response.data.data.company || {};
      const credentials = toCredentials(company);

      setCompanyName(company.name || '');
      setSaved(credentials);
      form.reset(credentials);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Could not load the Ghost settings. Please refresh the page.');
    } finally {
      setBusy(null);
    }
  };

  const handleSubmit = async (values) => {
    const credentials = toCredentials(values);
    setBusy('save');
    try {
      const response = await axios.post(`${baseUrl}/rest/organization/update`, credentials, {
        headers: {
          common: {
            AuthAPI: `Bearer ${storageService.getToken()}`,
          },
        },
      });

      if (response.data?.success === false) {
        toast.error(response.data?.messages || 'Could not save the Ghost settings.');
        return;
      }

      setSaved(credentials);
      form.reset(credentials);
      toast.success(
        isConnected(credentials) ? 'Ghost settings saved' : 'Ghost disconnected for this company',
      );
    } catch (error) {
      console.error('Error updating data:', error);
      toast.error('Could not save the Ghost settings.');
    } finally {
      setBusy(null);
    }
  };

  const syncGhostContent = async (isReset = false) => {
    // Nothing to sync until the company has its own Ghost site saved
    if (!connected) {
      toast.error('Add your Ghost API URL and Admin API Key, then click Save.');
      form.setFocus(saved.ghost_api_url ? 'ghost_admin_api_key' : 'ghost_api_url');
      return;
    }
    if (hasUnsavedChanges) {
      toast.error('Save your Ghost settings first.');
      return;
    }
    if (
      isReset &&
      !window.confirm(
        'This deletes the jobs and company tags Holedo has published to Ghost, then publishes them again. Blog posts and members are not affected. Continue?',
      )
    ) {
      return;
    }

    setBusy(isReset ? 'reset' : 'sync');
    try {
      const response = await axios.post(
        `${baseUrl}/rest/company/sync-ghost`,
        {
          isReset: isReset,
        },
        {
          // Includes the active workspace so workspace clients are synced too
          headers: authHeader(),
        },
      );

      if (response.data?.success === false) {
        toast.error(response.data?.messages || 'Could not start the Ghost sync.');
        return;
      }

      toast.success(
        isReset
          ? 'Ghost reset started. Jobs and company tags will be republished in a few minutes.'
          : 'Ghost sync started. Changes will appear in Ghost in a few minutes.',
      );
    } catch (error) {
      console.error('Error syncing ghost content:', error);
      toast.error('Could not start the Ghost sync.');
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Stay clickable without credentials so the tap explains what is missing
  const syncDisabled = !!busy;
  const syncHint = !connected
    ? 'Add and save your Ghost credentials to enable syncing'
    : hasUnsavedChanges
    ? 'Save your changes before syncing'
    : undefined;

  return (
    <div className="integration-container">
      <div className="card p-3">
        <h2 className="card-title">Integration</h2>
        <div className="card-section">
          <p className="text-muted mb-2">
            Ghost settings are shared by everyone in {companyName || 'your company'}.
          </p>
          {busy !== 'load' && !connected && (
            <div className="alert alert-warning mb-0" role="alert">
              Ghost is not connected yet. Enter your Ghost site URL and Admin API Key (Ghost
              Admin → Settings → Integrations → Add custom integration), then click Save.
            </div>
          )}
          <FormProvider {...form}>
            <Form onSubmit={form.handleSubmit(handleSubmit)}>
              <TextInput
                name="ghost_api_url"
                label="Ghost API URL"
                placeHolder="https://jobs.example.com"
                required
              />
              <TextInput
                name="ghost_admin_api_key"
                label="Ghost Admin API Key"
                placeHolder="id:secret"
                required
              />
              <TextInput name="ghost_content_api_key" label="Ghost Content API Key" />
              <div className="form-group mt-sm-3 mt-2">
                <button
                  className="btn btn-primary px-4 float-end"
                  type="submit"
                  disabled={!!busy}
                >
                  {busy === 'save' ? 'Saving...' : 'Save'}
                </button>

                <button
                  className="btn btn-primary px-4 float-end me-2"
                  type="button"
                  disabled={syncDisabled}
                  title={syncHint}
                  onClick={() => syncGhostContent(true)}
                >
                  {busy === 'reset' ? 'Resetting...' : 'Reset Ghost Content'}
                </button>

                <button
                  className="btn btn-primary px-4 float-end me-2"
                  type="button"
                  disabled={syncDisabled}
                  title={syncHint}
                  onClick={() => syncGhostContent()}
                >
                  {busy === 'sync' ? 'Syncing...' : 'Sync Ghost Content'}
                </button>
              </div>
            </Form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default IntegrationPage;
